import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Paper,
    Chip,
    TextField,
    MenuItem,
    Grid,
    Divider,
    InputAdornment,
    Collapse,
    Button,
    IconButton,
    Badge,
    Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useNavigate, useLocation } from 'react-router-dom';
import { getEditorialIntelligences, getEditorialIntelligenceFilters, getEditorialIntelligenceUserFilters } from '../../../utils/api';
import { createNotification } from '../../../utils/alerts';
import {
    buildSectorFilterGroups,
    expandSectorSelections,
    isSectorGroupSelected,
    toggleSectorGroupSelection,
} from '../../../utils/sectorFilterGroups';
import ResponsiveAdminPage from '../../../components/admin/ResponsiveAdminPage';
import ResponsiveDataView from '../../../components/admin/ResponsiveDataView';
import './editorial-intelligence.scss';

// ── Paleta ────────────────────────────────────────────────────────────────────
const PRIMARY      = '#f25d27';
const PRIMARY_DARK = '#d44e1f';

// ── Helpers ───────────────────────────────────────────────────────────────────
const normalizeArray = (value) => (Array.isArray(value) ? value : []);

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

// ── Estilos dos filtros rápidos (idênticos ao admin/dashboard) ────────────────
const miniChipSx = (selected, disabled = false) => ({
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'inline-flex', alignItems: 'center',
    px: 1.2, py: 0.3, borderRadius: '16px', border: '1px solid',
    fontSize: '0.73rem', fontWeight: selected ? 600 : 500,
    lineHeight: 1.4, userSelect: 'none',
    opacity: disabled ? 0.45 : 1,
    transition: 'all 0.15s',
    borderColor: selected ? PRIMARY : '#e3e3e3',
    backgroundColor: selected ? PRIMARY : 'transparent',
    color: selected ? '#fff' : '#666',
    '&:hover': disabled ? {} : {
        borderColor: PRIMARY,
        backgroundColor: selected ? PRIMARY_DARK : `${PRIMARY}10`,
        color: selected ? '#fff' : PRIMARY,
    },
});

const FILTER_LABEL_SX = {
    fontWeight: 700, color: '#666', fontSize: '0.67rem',
    textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.8,
};

const ChipRow = ({ label, children }) => (
    <Box>
        <Typography variant="caption" sx={FILTER_LABEL_SX}>{label}</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>{children}</Box>
    </Box>
);

const CompactMetaField = ({ label, value }) => (
    <Typography sx={{ fontSize: '0.74rem', color: '#555', lineHeight: 1.3 }}>
        <Box component="span" sx={{ fontWeight: 700, color: '#8a8a8a' }}>
            {label}:
        </Box>{' '}
        {value || '—'}
    </Typography>
);

const EditorialCompactCard = ({ row, onClick }) => {
    return (
        <Paper
            elevation={0}
            onClick={onClick}
            sx={{
                p: 1.2,
                borderRadius: '8px',
                border: '1px solid #ececec',
                cursor: onClick ? 'pointer' : 'default',
                '&:hover': onClick ? { backgroundColor: `${PRIMARY}08` } : undefined,
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#8a8a8a' }}>
                        Fonte:
                    </Typography>
                    <Chip
                        label={row.source || '—'}
                        size="small"
                        sx={{
                            backgroundColor: PRIMARY,
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            height: 22,
                            maxWidth: 'calc(100% - 56px)',
                            '& .MuiChip-label': { px: 1.1, overflow: 'hidden', textOverflow: 'ellipsis' },
                        }}
                    />
                </Box>

                <Typography sx={{ fontSize: '0.78rem', color: '#222', lineHeight: 1.35 }}>
                    <Box component="span" sx={{ fontWeight: 700, color: '#8a8a8a' }}>
                        Titulo:
                    </Box>{' '}
                    <Box
                        component="span"
                        sx={{
                            fontWeight: 600,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            verticalAlign: 'top',
                        }}
                    >
                        {row.title || '—'}
                    </Box>
                </Typography>

                <CompactMetaField label="Tema" value={row.theme || '—'} />

                <CompactMetaField label="Radar" value={row.radar_keyword || '—'} />

                <ClassificationTags row={row} />

                <CompactMetaField label="Data de publicação" value={formatDate(row.published_at)} />
            </Box>
        </Paper>
    );
};

const dateFieldSx = {
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
    '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY },
};

// ── Tags de classificação (Setor + Recomendação + Materialidade) ─────────────
// Usadas tanto na coluna "sem label" da tabela quanto no card mobile, para que
// ambas as visualizações fiquem consistentes. Cada informação é exibida como
// um badge próprio, um abaixo do outro, com o rótulo indicando o que ela representa.
const classificationBadgeSx = {
    height: 22,
    fontSize: '0.72rem',
    '& .MuiChip-label': { px: 1 },
};

const ClassificationField = ({ label, value, sx }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
        <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#8a8a8a' }}>
            {label}:
        </Typography>
        <Chip label={value || '—'} size="small" variant="outlined" sx={{ ...classificationBadgeSx, ...sx }} />
    </Box>
);

// `stacked` força Setor/Recomendação/Materialidade uma abaixo da outra,
// independentemente da largura disponível — usado apenas na tabela. No card
// mobile o padrão (lado a lado, com quebra) é mantido.
const ClassificationTags = ({ row, stacked = false }) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: stacked ? 'column' : 'row',
            flexWrap: stacked ? 'nowrap' : 'wrap',
            alignItems: stacked ? 'flex-start' : 'center',
            gap: stacked ? 0.5 : 0.8,
        }}
    >
        <ClassificationField label="Setor" value={row.sector} />
        <ClassificationField
            label="Recomendação"
            value={row.publication_recommendation}
            sx={{ color: PRIMARY, borderColor: PRIMARY }}
        />
        <ClassificationField
            label="Materialidade"
            value={row.materiality_level}
            sx={{ backgroundColor: '#eee' }}
        />
    </Box>
);

// ── Colunas da tabela ─────────────────────────────────────────────────────────
const COLUMNS = [
    {
        id: 'source',
        field: 'source',
        label: 'Fonte',
        minWidth: 120,
        render: (row) => (
            <Chip
                label={row.source}
                size="small"
                sx={{ backgroundColor: PRIMARY, color: '#fff', fontWeight: 600, fontSize: '0.75rem' }}
            />
        ),
    },
    { id: 'title', field: 'title', label: 'Título', minWidth: 220 },
    { id: 'theme', field: 'theme', label: 'Tema', minWidth: 140, render: (row) => row.theme || '—' },
    {
        id: 'published_at',
        field: 'published_at',
        label: 'Data de publicação',
        minWidth: 155,
        render: (row) => formatDate(row.published_at),
    },
    {
        id: 'radar_keyword',
        field: 'radar_keyword',
        label: 'Radar',
        minWidth: 120,
        sortable: false,
        render: (row) => row.radar_keyword || '—',
    },
    {
        id: 'classification',
        field: null,
        label: 'Classificações',
        minWidth: 220,
        sortable: false,
        render: (row) => <ClassificationTags row={row} stacked />,
    },
];

// ── Componente principal ──────────────────────────────────────────────────────
export default function AdminEditorialIntelligencePage() {
    const navigate  = useNavigate();
    const location  = useLocation();
    const isMobile  = useMediaQuery('(max-width:600px)');
    const isQuickFiltersCompact = useMediaQuery('(max-width:1024px)');

    // Filtros pré-selecionados vindos do Dashboard (via navigate state)
    const navState = location.state || {};

    // dados
    const [items, setItems]   = useState([]);
    const [total, setTotal]   = useState(0);
    const [loading, setLoading] = useState(true);

    // Indica que os filtros (persistidos no servidor, quando existirem, e as
    // opções disponíveis) já foram carregados e aplicados. Enquanto for
    // falso, a busca dos itens fica em espera, para que seja disparada
    // apenas uma vez, já com os filtros corretos.
    const [filtersInitialized, setFiltersInitialized] = useState(() => Boolean(location.state));

    // opções dos filtros rápidos
    const [filterOptions, setFilterOptions] = useState({
        sources: [],
        sectors: [],
        publication_recommendation_primary_areas: [],
        materiality_levels: [],
        radar_keywords: [],
    });
    const [filtersLoading, setFiltersLoading] = useState(false);

    // busca + filtros avançados — inicializados pelo navState quando presente
    const [filtersOpen, setFiltersOpen]       = useState(navState.filtersOpen ?? !!(navState.publishAtStart || navState.publishAtEnd));
    const [quickFiltersOpen, setQuickFiltersOpen] = useState(false);
    const [searchInput, setSearchInput]       = useState(navState.search || '');
    const [search, setSearch]                 = useState(navState.search || '');
    const [publishAtStart, setPublishAtStart] = useState(navState.publishAtStart || '');
    const [publishAtEnd, setPublishAtEnd]     = useState(navState.publishAtEnd   || '');

    // filtros rápidos — inicializados pelo navState quando presente
    const [selectedSources,       setSelectedSources]       = useState(navState.sources         || []);
    const [selectedSectors,       setSelectedSectors]       = useState(navState.sectors         || []);
    const [selectedPubAreas,      setSelectedPubAreas]      = useState(navState.recommendations || []);
    const [selectedRadarKeywords, setSelectedRadarKeywords] = useState(navState.radarKeywords || []);

    // filtros avançados adicionais
    const [selectedMaterialityLevel, setSelectedMaterialityLevel] = useState('');
    const [selectedIsRadar, setSelectedIsRadar] = useState(
        navState.isRadar === true ? 'true' : navState.isRadar === false ? 'false' : navState.isRadar || ''
    );
    const [selectedHasArticles, setSelectedHasArticles] = useState(
        navState.hasArticles === true ? 'true' : navState.hasArticles === false ? 'false' : navState.hasArticles || ''
    );

    // ordenação e paginação — restauradas pelo navState quando presente
    const [orderBy,        setOrderBy]        = useState(navState.orderBy        || 'published_at');
    const [orderDirection, setOrderDirection] = useState(navState.orderDirection || 'desc');
    const [page,     setPage]     = useState(navState.page     ?? 0);
    const [pageSize, setPageSize] = useState(navState.pageSize || 10);

    // Guarda o valor anterior de searchInput para detectar mudanças reais
    // (evita resetar a página no mount mesmo com StrictMode duplo-render)
    const prevSearchInputRef = useRef(searchInput);

    const sectorFilterGroups = useMemo(
        () => buildSectorFilterGroups(filterOptions.sectors),
        [filterOptions.sectors]
    );
    const expandedSelectedSectors = useMemo(
        () => expandSectorSelections(selectedSectors, filterOptions.sectors),
        [selectedSectors, filterOptions.sectors]
    );

    // badge de filtros avançados ativos
    const advancedFiltersCount = [publishAtStart, publishAtEnd].filter(Boolean).length
        + (selectedMaterialityLevel ? 1 : 0)
        + (selectedIsRadar !== '' ? 1 : 0)
        + (selectedHasArticles !== '' ? 1 : 0);
    const quickFiltersCount    = (searchInput ? 1 : 0)
                               + (selectedSources.length ? 1 : 0)
                               + (expandedSelectedSectors.length ? 1 : 0)
                               + (selectedPubAreas.length ? 1 : 0)
                               + (selectedRadarKeywords.length ? 1 : 0);
    const totalActiveFilters   = advancedFiltersCount + quickFiltersCount;

    // Ao acessar a tela (sem estado vindo de outra navegação interna), busca
    // primeiro os filtros salvos pelo usuário no servidor e, com a resposta
    // em mãos, busca as opções disponíveis para os filtros. Só então os
    // filtros salvos são aplicados ao estado local, para que a busca de itens
    // já saia com os filtros corretos.
    useEffect(() => {
        let cancelled = false;

        const applyPersistedFilters = (data) => {
            if (!data) return;
            setSelectedSources(normalizeArray(data.sources));
            setSelectedSectors(normalizeArray(data.sectors));
            setSelectedPubAreas(normalizeArray(data.publication_recommendation_primary_areas));
            setSelectedRadarKeywords(normalizeArray(data.radar_keywords));
            setSelectedMaterialityLevel(typeof data.materiality_level === 'string' ? data.materiality_level : '');
            setSelectedIsRadar(data.is_radar === true ? 'true' : data.is_radar === false ? 'false' : '');
            setPage(0);
        };

        const init = async () => {
            let persistedFilters = null;
            if (!location.state) {
                try {
                    persistedFilters = await getEditorialIntelligenceUserFilters();
                } catch {
                    persistedFilters = null;
                }
            }

            setFiltersLoading(true);
            try {
                const data = await getEditorialIntelligenceFilters();
                if (!cancelled) {
                    setFilterOptions({
                        sources:  data.sources  ?? [],
                        sectors:  data.sectors  ?? [],
                        publication_recommendation_primary_areas: data.publication_recommendation_primary_areas ?? [],
                        materiality_levels: data.materiality_levels ?? [],
                        radar_keywords: data.radar_keywords ?? [],
                    });
                }
            } catch {
                if (!cancelled) createNotification('error', 'Erro ao carregar filtros.', isMobile);
            } finally {
                if (!cancelled) setFiltersLoading(false);
            }

            if (!cancelled && persistedFilters) {
                applyPersistedFilters(persistedFilters);
            }
            if (!cancelled) setFiltersInitialized(true);
        };

        init();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // debounce na busca por texto — só reseta a página se o valor realmente mudou
    // (comparando com o valor anterior), o que garante comportamento correto
    // mesmo com React.StrictMode que monta o componente duas vezes
    useEffect(() => {
        const prev = prevSearchInputRef.current;
        prevSearchInputRef.current = searchInput;
        if (prev === searchInput) return;
        const timer = setTimeout(() => {
            setSearch(searchInput);
            setPage(0);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        if (!filtersInitialized) return;
        fetchItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtersInitialized, page, pageSize, search, selectedSources, expandedSelectedSectors, selectedPubAreas, selectedRadarKeywords, publishAtStart, publishAtEnd, orderBy, orderDirection, selectedMaterialityLevel, selectedIsRadar, selectedHasArticles]);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const params = {
                order_by: orderBy,
                order_direction: orderDirection,
                page,
                page_size: pageSize,
                // Persiste no servidor a configuração de filtros atual desta tela,
                // para restaurá-la em outro navegador/computador na próxima visita.
                update_user_filters: true,
            };
            if (search)                      params.search = search;
            if (selectedSources.length)      params.sources  = selectedSources;
            if (expandedSelectedSectors.length) params.sectors  = expandedSelectedSectors;
            if (selectedPubAreas.length)     params.publication_recommendation_primary_areas = selectedPubAreas;
            if (selectedRadarKeywords.length) params.radar_keywords = selectedRadarKeywords;
            if (publishAtStart)              params.publish_at_start = `${publishAtStart}T00:00:00`;
            if (publishAtEnd)                params.publish_at_end   = `${publishAtEnd}T23:59:59`;
            if (selectedMaterialityLevel)    params.materiality_level = selectedMaterialityLevel;
            if (selectedIsRadar !== '')      params.is_radar = selectedIsRadar === 'true';
            if (selectedHasArticles !== '')  params.has_articles = selectedHasArticles === 'true';

            const data = await getEditorialIntelligences(params);
            setItems(data.editorial_intelligences);
            setTotal(data.total);
        } catch (err) {
            if (err.response?.status !== 401) {
                createNotification('error', 'Erro ao carregar artigos.', isMobile);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (field) => {
        if (orderBy === field) {
            setOrderDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setOrderBy(field);
            setOrderDirection('asc');
        }
        setPage(0);
    };

    const handleClearAll = () => {
        setSearchInput('');
        setSearch('');
        setPublishAtStart('');
        setPublishAtEnd('');
        setSelectedSources([]);
        setSelectedSectors([]);
        setSelectedPubAreas([]);
        setSelectedRadarKeywords([]);
        setSelectedMaterialityLevel('');
        setSelectedIsRadar('');
        setSelectedHasArticles('');
        setPage(0);
    };

    const handleResetAdvanced = () => {
        setPublishAtStart('');
        setPublishAtEnd('');
        setSelectedMaterialityLevel('');
        setSelectedIsRadar('');
        setSelectedHasArticles('');
        setPage(0);
    };

    const handleRefresh = () => setPage((p) => p);  // força re-fetch

    // helper de toggle para multi-seleção
    const makeToggle = (selected, setSelected) => (v) => {
        const next = selected.includes(v)
            ? selected.filter((x) => x !== v)
            : [...selected, v];
        setSelected(next);
        setPage(0);
    };

    const openDetail = (row) => {
        if (!row?.id) return;
        navigate(`/admin/editorial-intelligence/${row.id}`, {
            state: {
                publishAtStart,
                publishAtEnd,
                sources: selectedSources,
                sectors: expandedSelectedSectors,
                recommendations: selectedPubAreas,
                radarKeywords: selectedRadarKeywords,
                isRadar: selectedIsRadar,
                hasArticles: selectedHasArticles,
                search: searchInput,
                orderBy,
                orderDirection,
                page,
                pageSize,
                filtersOpen,
            },
        });
    };

    return (
        <ResponsiveAdminPage>
            {/* ── Cabeçalho ─────────────────────────────────────────────── */}
            <Box sx={{ mb: 2 }}>
                {/* Linha 1: Título */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.4 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a', lineHeight: 1 }}>
                        Inteligência Editorial
                    </Typography>
                </Box>

                {/* Linha 2: subtítulo */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: '#999', fontSize: '0.78rem' }}>
                        Artigos monitorados e classificados
                    </Typography>

                    {/* Linha 3: botões */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        {totalActiveFilters > 0 && (
                            <Tooltip title="Limpar todos os filtros">
                                <Button
                                    size="small"
                                    onClick={handleClearAll}
                                    startIcon={<CloseIcon sx={{ fontSize: 15 }} />}
                                    sx={{
                                        textTransform: 'none', color: '#888', fontSize: '0.78rem',
                                        '&:hover': { backgroundColor: '#f5f5f5', color: '#444' },
                                    }}
                                >
                                    Limpar
                                </Button>
                            </Tooltip>
                        )}
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', ml: 'auto' }}>
                            {isQuickFiltersCompact && (
                                <Badge
                                    badgeContent={quickFiltersCount}
                                    color="primary"
                                    sx={{ '& .MuiBadge-badge': { backgroundColor: PRIMARY, fontSize: '0.62rem', minWidth: 16, height: 16 } }}
                                >
                                    <Button
                                        size="small"
                                        startIcon={<FilterListIcon />}
                                        onClick={() => setQuickFiltersOpen((prev) => !prev)}
                                        variant={quickFiltersOpen ? 'contained' : 'outlined'}
                                        sx={quickFiltersOpen
                                            ? { backgroundColor: PRIMARY, color: '#fff', '&:hover': { backgroundColor: PRIMARY_DARK }, textTransform: 'none', boxShadow: 'none' }
                                            : { borderColor: '#ddd', color: '#555', textTransform: 'none', '&:hover': { borderColor: PRIMARY, color: PRIMARY, backgroundColor: `${PRIMARY}08` } }
                                        }
                                    >
                                        Filtros rápidos
                                    </Button>
                                </Badge>
                            )}
                        <Badge
                            badgeContent={advancedFiltersCount}
                            color="primary"
                            sx={{ '& .MuiBadge-badge': { backgroundColor: PRIMARY, fontSize: '0.62rem', minWidth: 16, height: 16 } }}
                        >
                            <Button
                                size="small"
                                startIcon={<FilterListIcon />}
                                onClick={() => setFiltersOpen((o) => !o)}
                                variant={filtersOpen ? 'contained' : 'outlined'}
                                sx={filtersOpen
                                    ? { backgroundColor: PRIMARY, color: '#fff', '&:hover': { backgroundColor: PRIMARY_DARK }, textTransform: 'none', boxShadow: 'none' }
                                    : { borderColor: '#ddd', color: '#555', textTransform: 'none', '&:hover': { borderColor: PRIMARY, color: PRIMARY, backgroundColor: `${PRIMARY}08` } }
                                }
                            >
                                Filtros avançados
                            </Button>
                        </Badge>
                        <Tooltip title="Atualizar dados">
                            <IconButton
                                onClick={handleRefresh}
                                size="small"
                                disabled={loading}
                                sx={{
                                    border: '1px solid #ddd', borderRadius: '6px',
                                    '&:hover': { borderColor: PRIMARY, color: PRIMARY, backgroundColor: `${PRIMARY}08` },
                                }}
                            >
                                <RefreshIcon fontSize="small" sx={{
                                    animation: loading ? 'spin 1s linear infinite' : 'none',
                                    '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } },
                                }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Configurações">
                            <IconButton
                                onClick={() => navigate('/admin/config/editorial-intelligence')}
                                size="small"
                                aria-label="Configurações"
                                sx={{
                                    border: '1px solid #ddd', borderRadius: '6px',
                                    '&:hover': { borderColor: PRIMARY, color: PRIMARY, backgroundColor: `${PRIMARY}08` },
                                }}
                            >
                                <SettingsIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* ── Filtros Avançados (colapsável) ────────────────────────── */}
            <Collapse in={filtersOpen}>
                <Paper sx={{
                    p: 2.5, mb: 2.5, borderRadius: '12px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0',
                }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#333', mb: 1.8, fontSize: '0.85rem' }}>
                        Filtros Avançados
                    </Typography>
                    <Grid container spacing={1.5} alignItems="flex-end">
                        <Grid item xs={6} sm={3} md={2}>
                            <TextField
                                label="De"
                                type="date"
                                size="small"
                                fullWidth
                                value={publishAtStart}
                                onChange={(e) => { setPublishAtStart(e.target.value); setPage(0); }}
                                InputLabelProps={{ shrink: true }}
                                sx={dateFieldSx}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3} md={2}>
                            <TextField
                                label="Até"
                                type="date"
                                size="small"
                                fullWidth
                                value={publishAtEnd}
                                onChange={(e) => { setPublishAtEnd(e.target.value); setPage(0); }}
                                InputLabelProps={{ shrink: true }}
                                sx={dateFieldSx}
                            />
                        </Grid>
                        <Grid item xs={6} sm={3} md={2}>
                            <TextField
                                select
                                label="Radar"
                                size="small"
                                fullWidth
                                value={selectedIsRadar}
                                onChange={(e) => { setSelectedIsRadar(e.target.value); setPage(0); }}
                                sx={dateFieldSx}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="true">Sim</MenuItem>
                                <MenuItem value="false">Não</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={6} sm={3} md={2}>
                            <TextField
                                select
                                label="Matéria escrita"
                                size="small"
                                fullWidth
                                value={selectedHasArticles}
                                onChange={(e) => { setSelectedHasArticles(e.target.value); setPage(0); }}
                                sx={dateFieldSx}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="true">Sim</MenuItem>
                                <MenuItem value="false">Não</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={6} sm={3} md={2}>
                            <TextField
                                select
                                label="Materialidade"
                                size="small"
                                fullWidth
                                value={selectedMaterialityLevel}
                                onChange={(e) => { setSelectedMaterialityLevel(e.target.value); setPage(0); }}
                                sx={dateFieldSx}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                {filterOptions.materiality_levels.map((o) => (
                                    <MenuItem key={o.value} value={o.value}>{o.text}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <Divider sx={{ mb: 1.5 }} />
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Button
                                    size="small"
                                    onClick={handleResetAdvanced}
                                    sx={{ color: '#888', textTransform: 'none' }}
                                >
                                    Resetar
                                </Button>
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => setFiltersOpen(false)}
                                    sx={{ backgroundColor: PRIMARY, '&:hover': { backgroundColor: PRIMARY_DARK }, textTransform: 'none', boxShadow: 'none' }}
                                >
                                    Fechar
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            </Collapse>

            <Collapse in={!isQuickFiltersCompact || quickFiltersOpen}>
                <Paper sx={{ p: 2, mb: 2.5, borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,.06)', border: '1px solid #f0f0f0' }}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Pesquise por título, tema ou palavras chaves"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#bbb', fontSize: 18 }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: PRIMARY },
                        }}
                    />

                {/* Linha 1: Fonte */}
                <ChipRow label="Fonte">
                    <Box component="span"
                        onClick={() => { setSelectedSources([]); setPage(0); }}
                        sx={miniChipSx(!selectedSources.length, filtersLoading)}>
                        Todas
                    </Box>
                    {filterOptions.sources.map((o, idx) => {
                        const v   = o.value ?? o;
                        const txt = o.text  ?? o;
                        const sel = selectedSources.includes(v);
                        return (
                            <Box key={idx} component="span"
                                sx={miniChipSx(sel, filtersLoading)}
                                onClick={() => !filtersLoading && makeToggle(selectedSources, setSelectedSources)(v)}>
                                {txt}
                            </Box>
                        );
                    })}
                </ChipRow>

                <Divider sx={{ my: 2 }} />

                {/* Linha 2: Radar Keywords */}
                <ChipRow label="Radar">
                    <Box component="span"
                        onClick={() => { setSelectedRadarKeywords([]); setPage(0); }}
                        sx={miniChipSx(!selectedRadarKeywords.length, filtersLoading)}>
                        Todos
                    </Box>
                    {filterOptions.radar_keywords.map((o, idx) => {
                        const v   = o.value ?? o;
                        const txt = o.text  ?? o;
                        const sel = selectedRadarKeywords.includes(v);
                        return (
                            <Box key={idx} component="span"
                                sx={miniChipSx(sel, filtersLoading)}
                                onClick={() => !filtersLoading && makeToggle(selectedRadarKeywords, setSelectedRadarKeywords)(v)}>
                                {txt}
                            </Box>
                        );
                    })}
                </ChipRow>

                <Divider sx={{ my: 2 }} />

                {/* Linha 3: Recomendação */}
                <ChipRow label="Recomendação">
                    <Box component="span"
                        onClick={() => { setSelectedPubAreas([]); setPage(0); }}
                        sx={miniChipSx(!selectedPubAreas.length, filtersLoading)}>
                        Todas
                    </Box>
                    {filterOptions.publication_recommendation_primary_areas.map((o, idx) => {
                        const v   = o.value ?? o;
                        const txt = o.text  ?? o;
                        const sel = selectedPubAreas.includes(v);
                        return (
                            <Box key={idx} component="span"
                                sx={miniChipSx(sel, filtersLoading)}
                                onClick={() => !filtersLoading && makeToggle(selectedPubAreas, setSelectedPubAreas)(v)}>
                                {txt}
                            </Box>
                        );
                    })}
                </ChipRow>

                <Divider sx={{ my: 2 }} />

                    {/* Linha 4: Setor */}
                    <ChipRow label="Setor">
                        <Box component="span"
                            onClick={() => { setSelectedSectors([]); setPage(0); }}
                            sx={miniChipSx(!expandedSelectedSectors.length, filtersLoading)}>
                            Todos
                        </Box>
                        {sectorFilterGroups.map((group) => {
                            const sel = isSectorGroupSelected(selectedSectors, group);
                            return (
                                <Box key={group.key} component="span"
                                    sx={miniChipSx(sel, filtersLoading)}
                                    onClick={() => {
                                        if (filtersLoading) return;
                                        setSelectedSectors((selected) => toggleSectorGroupSelection(selected, group));
                                        setPage(0);
                                    }}>
                                    {group.label}
                                </Box>
                            );
                        })}
                    </ChipRow>
                </Paper>
            </Collapse>

            {/* ── Tabela ─────────────────────────────────────────────────── */}
            <Paper sx={{
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                border: '1px solid #f0f0f0',
                p: isQuickFiltersCompact ? 1 : 0,
            }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress sx={{ color: PRIMARY }} />
                    </Box>
                ) : (
                    <ResponsiveDataView
                        columns={COLUMNS}
                        rows={items}
                        orderBy={orderBy}
                        orderDirection={orderDirection}
                        onSort={handleSort}
                        emptyMessage="Nenhuma inteligência editorial encontrada."
                        mobileBreakpoint={1360}
                        mobileCardRenderer={(row, index) => (
                            <EditorialCompactCard
                                key={row.id ?? index}
                                row={row}
                                onClick={() => openDetail(row)}
                            />
                        )}
                        onRowClick={openDetail}
                        page={page}
                        pageSize={pageSize}
                        total={total}
                        onPageChange={setPage}
                        onPageSizeChange={(size) => { setPageSize(size); setPage(0); }}
                        paperSx={{ borderRadius: '8px', border: 'none', boxShadow: 'none' }}
                    />
                )}
            </Paper>
        </ResponsiveAdminPage>
    );
}
